import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { authApi } from "@/api/auth";
import type { LoginRequest, RegisterRequest, SocialLoginRequest } from "@/types";

export function useAuth() {
  const { user, token, isAuthenticated, isAdmin, isController, setTokens, login: storeLogin, logout: storeLogout, setUser } =
    useAuthStore();
  const queryClient = useQueryClient();

  const { data: currentUser } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => authApi.getMe(),
    enabled: !!token && !user,
    retry: false,
  });

  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
    }
  }, [currentUser, setUser]);

  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: async (response) => {
      setTokens(response.access_token, response.refresh_token);
      const userData = await authApi.getMe();
      storeLogin(userData, response.access_token, response.refresh_token);
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });

  const registerMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: async (response) => {
      setTokens(response.access_token, response.refresh_token);
      const userData = await authApi.getMe();
      storeLogin(userData, response.access_token, response.refresh_token);
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });

  const socialLoginMutation = useMutation({
    mutationFn: (data: SocialLoginRequest) => authApi.socialLogin(data),
    onSuccess: async (response) => {
      setTokens(response.access_token, response.refresh_token);
      const userData = await authApi.getMe();
      storeLogin(userData, response.access_token, response.refresh_token);
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });

  const logout = () => {
    storeLogout();
    queryClient.clear();
  };

  return {
    user,
    token,
    isAuthenticated,
    isAdmin,
    isController,
    login: loginMutation.mutateAsync,
    loginError: loginMutation.error,
    isLoggingIn: loginMutation.isPending,
    register: registerMutation.mutateAsync,
    registerError: registerMutation.error,
    isRegistering: registerMutation.isPending,
    socialLogin: socialLoginMutation.mutateAsync,
    socialLoginError: socialLoginMutation.error,
    isSocialLoggingIn: socialLoginMutation.isPending,
    logout,
  };
}

export function useRequireAuth() {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated && !token) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, token, navigate]);

  return { isAuthenticated };
}
